import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { FeatureFlagValue, FEATURE_FLAG_DEFAULTS } from '@/common/domain/constants/featureFlags';

export interface UseFeatureFlagsResult {
  values: Record<string, boolean>;
  isReady: boolean;
}

/**
 * Read multiple feature flags in a single React hook call and return a map of values.
 * Uses LaunchDarkly's built-in context to read flag values synchronously, eliminating
 * the UI blinking issue that occurred with async variation() calls.
 */
export const useFeatureFlags = (flags: FeatureFlagValue[]): UseFeatureFlagsResult => {
  const forceAllFlags = import.meta.env.VITE_LH === '1' && import.meta.env.VITE_LH_FORCE_FEATURE_FLAGS === 'true';

  const allFlags = useFlags();

  const values = useMemo(() => {
    if (forceAllFlags) {
      return Object.fromEntries(flags.map((flag) => [flag, true]));
    }
    return Object.fromEntries(
      flags.map((flag) => {
        if (Object.prototype.hasOwnProperty.call(allFlags, flag)) {
          return [flag, Boolean(allFlags[flag])];
        }
        return [flag, FEATURE_FLAG_DEFAULTS[flag] ?? false];
      })
    );
  }, [forceAllFlags, flags, allFlags]);

  return { values, isReady: true };
};
